import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import StockVoDashboard from './StockVoDashboard';
import { ALL_OFFER_AGE_BUCKETS, getOfferAgeBucket, isDormant } from '../utils/stockVoOfferAge';
import './DynamicDB.css';
import './StockVoSuivi.css';

// Onglet « Stock VO » (groupe Commerce VN/VO) — croise le stock VO courant
// avec la dernière offre VO émise par châssis (cf. backend/src/routes/stock-vo-suivi.js)
// pour identifier le stock dormant (sans offre récente) nécessitant une relance.

function joursDepuis(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

// Coût d'immobilisation du stock : prix d'achat (PurchasePrice, ~99 % renseigné
// en prod — même champ que « valeur du stock » du Reporting Financier Stock VO)
// × taux annuel × jours en stock / 365. Absent si prix d'achat non renseigné
// (ex. en local, cf. commentaire backend/src/routes/stock-vo-suivi.js).
const TAUX_FRAIS_FINANCIERS = 0.03;
function fraisFinanciers(prixAchat, joursStock) {
  const prix = parseFloat(prixAchat);
  if (!prixAchat || isNaN(prix) || joursStock === null) return null;
  return prix * TAUX_FRAIS_FINANCIERS * joursStock / 365;
}

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';
const truncate = (s, max) => (s && s.length > max) ? `${s.slice(0, max)}…` : s;
const fmtMoney = (v) => {
  const n = parseFloat(v);
  if (!v || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};

function KpiCard({ label, value, sublabel }) {
  return (
    <div className="svs-kpi-card">
      <div className="svs-kpi-label">{label}</div>
      <div className="svs-kpi-value">{value}</div>
      {sublabel && <div className="svs-kpi-sub">{sublabel}</div>}
    </div>
  );
}

export default function StockVoSuivi({ user, concessions = [], setHeaderAction }) {
  const [mainTab, setMainTab]     = useState('dashboard');
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [concFilter, setConcFilter] = useState('');
  const [marqueFilter, setMarqueFilter] = useState('');
  const [dormanceFilter, setDormanceFilter] = useState('');
  const [emplacementFilter, setEmplacementFilter] = useState('');
  const [sortKey, setSortKey]     = useState('jours_offre');
  const [sortDir, setSortDir]     = useState('desc');

  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/stock-vo-suivi').then(res => {
      if (!cancelled) setRows(res.data.entries || []);
    }).catch(err => {
      console.error('Erreur chargement stock-vo-suivi:', err);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const enriched = useMemo(() => rows.map(r => {
    const joursOffre = joursDepuis(r.last_offer_date);
    const joursStock = joursDepuis(r.date_entree);
    return {
      ...r,
      jours_offre: joursOffre,
      jours_stock: joursStock,
      dormance: getOfferAgeBucket(joursOffre),
      frais_financiers: fraisFinanciers(r.prix_achat, joursStock),
    };
  }), [rows]);

  const marques = useMemo(() => {
    const set = new Set(enriched.map(r => r.marque).filter(Boolean));
    return Array.from(set).sort();
  }, [enriched]);

  const emplacements = useMemo(() => {
    const set = new Set(enriched.map(r => r.parking_allee).filter(Boolean));
    return Array.from(set).sort();
  }, [enriched]);

  const concList = (concessions || []).filter(c => c.id && c.nom);

  const filtered = useMemo(() => {
    return enriched.filter(r => {
      if (concFilter && String(r.concession_id) !== String(concFilter)) return false;
      if (marqueFilter && r.marque !== marqueFilter) return false;
      if (dormanceFilter && r.dormance.key !== dormanceFilter) return false;
      if (emplacementFilter && r.parking_allee !== emplacementFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.chassis || ''} ${r.immat || ''} ${r.marque || ''} ${r.modele || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, concFilter, marqueFilter, dormanceFilter, emplacementFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (av === null || av === undefined) av = sortDir === 'desc' ? -Infinity : Infinity;
      if (bv === null || bv === undefined) bv = sortDir === 'desc' ? -Infinity : Infinity;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // KPIs calculés sur `filtered` (pas `enriched`) pour refléter les filtres actifs
  // (concession, marque, dormance, recherche) — les cartes décrivent ce qui est
  // affiché dans le tableau ci-dessous, pas l'ensemble du stock non filtré.
  const kpis = useMemo(() => {
    const totalStock = filtered.length;
    const avecOffre = filtered.filter(r => r.jours_offre !== null).length;
    const dormants = filtered.filter(r => isDormant(r.jours_offre)).length;
    const joursConnus = filtered.filter(r => r.jours_offre !== null).map(r => r.jours_offre);
    const ageMoyen = joursConnus.length ? Math.round(joursConnus.reduce((a, b) => a + b, 0) / joursConnus.length) : null;
    return { totalStock, avecOffre, dormants, ageMoyen };
  }, [filtered]);

  const cols = [
    { key: 'immat',        label: 'Immatriculation' },
    { key: 'marque',       label: 'Marque / Modèle' },
    { key: 'parking_allee', label: 'Emplacement parking' },
    { key: 'atelier_statut', label: '🔧 Suivi VN/VO' },
    { key: 'date_entree',  label: 'Date entrée' },
    { key: 'last_offer_date', label: 'Dernière offre' },
    { key: 'prix_achat',   label: 'Prix d\'achat' },
    { key: 'frais_financiers', label: 'Frais Fi.' },
    { key: 'last_offer_prix', label: 'Prix proposé' },
  ];

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={mainTab === 'dashboard' ? 'active' : ''} onClick={() => setMainTab('dashboard')}>📊 Tableau de bord</button>
        <button className={mainTab === 'detail' ? 'active' : ''} onClick={() => setMainTab('detail')}>📋 Détail</button>
      </div>

      {mainTab === 'dashboard' && (
        <StockVoDashboard
          enriched={enriched}
          loading={loading}
          concList={concList}
          concFilter={concFilter}
          setConcFilter={setConcFilter}
        />
      )}

      {mainTab === 'detail' && (<>
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {concList.length > 1 && (
          <select className="page-select" value={concFilter} onChange={e => setConcFilter(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concList.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
        {marques.length > 0 && (
          <select className="page-select" value={marqueFilter} onChange={e => setMarqueFilter(e.target.value)}>
            <option value="">Toutes marques</option>
            {marques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {emplacements.length > 0 && (
          <select className="page-select" value={emplacementFilter} onChange={e => setEmplacementFilter(e.target.value)}>
            <option value="">Tous emplacements</option>
            {emplacements.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
        <select className={`page-select ${dormanceFilter ? 'active-filter' : ''}`} value={dormanceFilter} onChange={e => setDormanceFilter(e.target.value)}>
          <option value="">Dernière offre</option>
          {ALL_OFFER_AGE_BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <span className="svs-count">{sorted.length} / {enriched.length} véhicule{enriched.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="svs-kpi-row">
        <KpiCard label="Stock VO total" value={loading ? '…' : kpis.totalStock} />
        <KpiCard
          label="Avec au moins une offre"
          value={loading ? '…' : `${kpis.totalStock ? Math.round(kpis.avecOffre / kpis.totalStock * 100) : 0} %`}
          sublabel={loading ? '' : `${kpis.avecOffre} véhicule${kpis.avecOffre !== 1 ? 's' : ''}`}
        />
        <KpiCard
          label="🔴 Stock dormant / jamais offert"
          value={loading ? '…' : kpis.dormants}
          sublabel="≥ 60 j sans offre, ou jamais offert"
        />
        <KpiCard
          label="Âge moyen depuis dernière offre"
          value={loading ? '…' : (kpis.ageMoyen !== null ? `${kpis.ageMoyen} j` : '—')}
        />
      </div>

      {loading ? (
        <div className="ddb-loading">Chargement...</div>
      ) : (
        <div className="table-container svs-table-wrapper">
          <table className="svs-table">
            <thead>
              <tr>
                {cols.map(c => (
                  <th key={c.key} className="svs-sortable" onClick={() => onSort(c.key)}>
                    {c.label}{sortKey === c.key && <span>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={cols.length} className="ddb-empty">Aucun véhicule en stock VO.</td></tr>
              ) : sorted.map(r => (
                <tr key={r.id}>
                  <td>
                    <span className="plaque-badge">{r.immat ? r.immat.replace(/[-\s]/g, '') : '—'}</span>
                  </td>
                  <td title={[r.marque, r.modele].filter(Boolean).join(' ') || undefined}>
                    {truncate([r.marque, r.modele].filter(Boolean).join(' '), 30) || '—'}
                  </td>
                  <td>{r.parking_allee ? <>🅿️ {r.parking_allee}</> : <span style={{ color: 'var(--text-muted)' }}>Non localisé</span>}</td>
                  <td
                    title={r.atelier_numero_or ? `N° OR ${r.atelier_numero_or}` : undefined}
                  >
                    {r.atelier_statut
                      ? r.atelier_statut
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>{fmtDate(r.date_entree)}</td>
                  <td>{fmtDate(r.last_offer_date)}</td>
                  <td>{fmtMoney(r.prix_achat)}</td>
                  <td title={r.prix_achat ? `${fmtMoney(r.prix_achat)} × 3 % × ${r.jours_stock} j / 365` : 'Prix d\'achat non renseigné'}>
                    {r.frais_financiers !== null ? fmtMoney(r.frais_financiers) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>{fmtMoney(r.last_offer_prix)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
